/**
 * Prefill from the person the form is FOR — Step 70.
 *
 * A field can carry a default written as a token — `{{requester.department}}`
 * — resolved at render time from the directory attributes Step 57 added, from
 * the person the form is FOR. An agent switching the requester picker gets
 * the new person's details rather than the last one's.
 *
 * ── Where this earns its place, and where it is actively wrong ─────────
 *
 * Kai, 2026-09-08: *"why would filling out a new starter form, why would the
 * person requesting the ticket's information be filled, they have an
 * account"*. Correct, and worth writing down because the obvious reading of
 * this feature is the wrong one.
 *
 * A New Starter form is about a person who has no account and no directory
 * row. `{{requester.first_name}}` on "New starter's first name" would put the
 * hiring manager's name in it — not a saving, a defect. The tokens are chosen
 * per field by an admin, and on that form the only sensible target is a field
 * genuinely about the MANAGER: "which team are they joining", which is nearly
 * always the requester's own.
 *
 * The rule that falls out, and the one to apply when adding a default: **if a
 * field would always hold the directory value and nobody would ever edit it,
 * delete the field — do not prefill it.** The ticket already knows who raised
 * it; asking them to confirm their own department is a question with one
 * legal answer. Prefill is for the fields where the directory value is
 * usually right and sometimes not — the cost centre to charge, the delivery
 * site, the number to ring about THIS request.
 *
 * ── Placeholders only, never free literals ─────────────────────────────
 *
 * The reference validates "must be a placeholder" and it is right to. A free
 * literal default is a value nobody chose that arrives pre-agreed: it looks
 * like the requester answered "London" when the truth is that an admin typed
 * London into a form template two years ago and everybody has pressed submit
 * ever since. A token is honestly attributed — it is that person's own
 * recorded attribute, and when the directory has nothing it resolves to
 * empty and the field is simply blank.
 *
 * ── Resolved on the SERVER ─────────────────────────────────────────────
 *
 * The browser is never handed the directory to interpolate from. It is given
 * the resolved value for the one person the form is about, which is the same
 * reason the rest of this codebase resolves `@me` server-side: the client
 * cannot ask for somebody else's attributes if it never holds them.
 */

/** What a token may address. Closed, because an open path is a way to read
 *  any column on `users` — including the ones deliberately never sent. */
export const PREFILL_TOKENS = [
  { token: 'requester.first_name', label: 'First name' },
  { token: 'requester.last_name', label: 'Last name' },
  { token: 'requester.full_name', label: 'Full name' },
  { token: 'requester.email', label: 'Email' },
  { token: 'requester.job_title', label: 'Job title' },
  { token: 'requester.department', label: 'Department' },
  { token: 'requester.location', label: 'Office location' },
  { token: 'requester.manager', label: 'Manager' },
  { token: 'requester.phone', label: 'Phone' },
  { token: 'requester.company', label: 'Company' },
] as const;

export type PrefillToken = (typeof PREFILL_TOKENS)[number]['token'];

const KNOWN = new Set<string>(PREFILL_TOKENS.map((t) => t.token));

/** The whole value must be exactly one token — `{{requester.email}}`, not
 *  `Hi {{requester.first_name}}`. A default is a VALUE, and a sentence with a
 *  hole in it is a template, which is a different feature. */
const EXACT = /^\{\{\s*([a-z_.]+)\s*\}\}$/;

export function isPrefillToken(value: string): boolean {
  const m = EXACT.exec(value.trim());
  return !!m && KNOWN.has(m[1]!);
}

/** Null when the default is acceptable, else why not — shown in the builder. */
export function prefillError(value: string): string | null {
  const v = value.trim();
  if (!v) return null; // No default at all is fine.
  const m = EXACT.exec(v);
  if (!m) return 'A default must be a placeholder like {{requester.department}}, not typed-in text.';
  if (!KNOWN.has(m[1]!)) return `There is no placeholder called {{${m[1]}}}.`;
  return null;
}

/** The directory attributes a token can read. Deliberately a narrow shape:
 *  what is not here cannot be addressed. */
export interface PrefillSource {
  givenName?: string | null;
  surname?: string | null;
  name?: string | null;
  email?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  officeLocation?: string | null;
  managerName?: string | null;
  mobilePhone?: string | null;
  businessPhone?: string | null;
  companyName?: string | null;
}

/**
 * What a token resolves to for one person, or `''` when the directory has
 * nothing.
 *
 * Empty rather than the token text: a form showing a literal
 * `{{requester.department}}` in a box is worse than an empty box, because
 * somebody will submit it.
 */
export function resolvePrefill(value: string, person: PrefillSource | null): string {
  const m = EXACT.exec(value.trim());
  if (!m || !KNOWN.has(m[1]!)) return '';
  if (!person) return '';
  const first = person.givenName ?? person.name?.split(' ')[0] ?? '';
  switch (m[1] as PrefillToken) {
    case 'requester.first_name':
      return first || '';
    case 'requester.last_name':
      return person.surname ?? person.name?.split(' ').slice(1).join(' ') ?? '';
    case 'requester.full_name':
      return person.name ?? '';
    case 'requester.email':
      return person.email ?? '';
    case 'requester.job_title':
      return person.jobTitle ?? '';
    case 'requester.department':
      return person.department ?? '';
    case 'requester.location':
      return person.officeLocation ?? '';
    case 'requester.manager':
      return person.managerName ?? '';
    // Mobile first: it is the number somebody answers.
    case 'requester.phone':
      return person.mobilePhone ?? person.businessPhone ?? '';
    case 'requester.company':
      return person.companyName ?? '';
    default:
      return '';
  }
}
